<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Button from '../../Button.svelte';
  import BackButton from '../../BackButton.svelte';
  import Table from '../../Table.svelte';
  import Pagination from '../../Pagination.svelte';
  import EmployeeDonationService, { type ExtendedCharity, type DonationRequest, type PaginationInfo } from '../../../lib/api/employee/donation.service';
  import { auth } from '../../../stores/auth.store';
  import DonationRequestModal from '../myDonations/DonationRequestModal.svelte';

  export let charityId: string = '';
  
  let loading = false;
  let error: string | null = null;
  let charity: ExtendedCharity | null = null;
  let currentCharityId: string = '';
  let isLoading = false; // Guard to prevent concurrent calls

  // Tab state
  let activeTab: 'donations' | 'payments' = 'donations';
  
  // Donation modal state
  let showDonationModal = false;

  // Donation requests state
  let donationRequests: DonationRequest[] = [];
  let donationsLoading = false;
  let donationsError: string | null = null;
  let donationsAttempted = false; // Track if we've attempted to load donations
  let donationsPaging: PaginationInfo = {
    current_page: 1,
    last_page: 1,
    per_page: 20,
    current_items: 0,
    from: 0,
    to: 0,
    total: 0
  };
  let donationsCurrentPage = 1;
  let donationsTotalPages = 1;

  // Payments state (completed/approved donations)
  let payments: DonationRequest[] = [];
  let paymentsLoading = false;
  let paymentsError: string | null = null;
  let paymentsAttempted = false; // Track if we've attempted to load payments
  let paymentsPaging: PaginationInfo = {
    current_page: 1,
    last_page: 1,
    per_page: 20,
    current_items: 0,
    from: 0,
    to: 0,
    total: 0
  };
  let paymentsCurrentPage = 1;
  let paymentsTotalPages = 1;

  async function loadCharityDetails(id: string) {
    if (!id) {
      error = 'No charity ID provided';
      loading = false;
      isLoading = false;
      return;
    }

    // Prevent duplicate calls and concurrent requests
    if (isLoading || (currentCharityId === id && charity)) {
      return;
    }

    isLoading = true;
    currentCharityId = id;
    loading = true;
    error = null;
    
    try {
      const response = await EmployeeDonationService.getCharity(id);
      
      if (response.success && response.data) {
        // Only update if this is still the current request
        if (currentCharityId === id) {
          charity = response.data;
          error = null; // Clear any previous errors
        }
      } else {
        // Only set error if this is still the current request
        if (currentCharityId === id) {
          error = response.message || 'Failed to load charity details';
        }
      }
    } catch (err: any) {
      // Only set error if this is still the current request
      if (currentCharityId === id) {
        console.error('Error loading charity details:', err);
        error = err.message || 'Failed to load charity details';
      }
    } finally {
      if (currentCharityId === id) {
        loading = false;
      }
      isLoading = false;
    }
  }

  async function loadDonationRequests(force = false) {
    if (!charityId) return;
    
    const user = auth.getUser();
    if (!user || !user.uuid) return;

    // Prevent concurrent requests or repeated calls unless forced
    if (donationsLoading || (donationsAttempted && !force)) return;

    donationsLoading = true;
    donationsError = null;
    donationsAttempted = true;

    try {
      const response = await EmployeeDonationService.getDonationRequests(user.uuid, {
        charity_uuid: charityId,
        page: donationsCurrentPage,
        per_page: 20,
        sort_by: 'created_at',
        sort_direction: 'desc'
      });

      if (response.success && response.data) {
        donationRequests = response.data;
        if (response.paging && typeof response.paging === 'object') {
          donationsPaging = response.paging;
          donationsTotalPages = response.paging.last_page ?? 1;
        }
        donationsError = null; // Clear any previous errors on success
      } else {
        donationsError = response.message || 'Failed to load donation requests';
      }
    } catch (err: any) {
      console.error('Error loading donation requests:', err);
      donationsError = err.message || 'Failed to load donation requests';
      // Don't reset donationsAttempted on error - prevent infinite retries
    } finally {
      donationsLoading = false;
    }
  }

  async function loadPayments(force = false) {
    if (!charityId) return;
    
    const user = auth.getUser();
    if (!user || !user.uuid) return;

    // Prevent concurrent requests or repeated calls unless forced
    if (paymentsLoading || (paymentsAttempted && !force)) return;

    paymentsLoading = true;
    paymentsError = null;
    paymentsAttempted = true;

    try {
      // Get all donation requests and filter for completed/approved ones
      // Use a larger per_page to get all and filter client-side, or use status filter if API supports it
      const response = await EmployeeDonationService.getDonationRequests(user.uuid, {
        charity_uuid: charityId,
        page: paymentsCurrentPage,
        per_page: 100, // Get more to filter properly
        sort_by: 'created_at',
        sort_direction: 'desc'
      });

      if (response.success && response.data) {
        // Filter to only show completed/approved/verified statuses
        const allPayments = response.data.filter(donation => {
          const status = donation.donation_request_status?.name?.toLowerCase() || '';
          return status === 'completed' || status === 'approved' || status === 'verified';
        });
        
        // Apply pagination to filtered results
        const startIndex = (paymentsCurrentPage - 1) * 20;
        const endIndex = startIndex + 20;
        payments = allPayments.slice(startIndex, endIndex);
        
        if (response.paging) {
          paymentsPaging = {
            ...response.paging,
            total: allPayments.length,
            current_items: payments.length,
            from: startIndex + 1,
            to: Math.min(endIndex, allPayments.length)
          };
          paymentsTotalPages = Math.ceil(allPayments.length / 20) || 1;
        }
        paymentsError = null; // Clear any previous errors on success
      } else {
        paymentsError = response.message || 'Failed to load payments';
      }
    } catch (err: any) {
      console.error('Error loading payments:', err);
      paymentsError = err.message || 'Failed to load payments';
      // Don't reset paymentsAttempted on error - prevent infinite retries
    } finally {
      paymentsLoading = false;
    }
  }

  function handleTabChange(tab: 'donations' | 'payments') {
    activeTab = tab;
    if (tab === 'donations' && donationRequests.length === 0 && !donationsLoading && !donationsAttempted) {
      loadDonationRequests();
    } else if (tab === 'payments' && payments.length === 0 && !paymentsLoading && !paymentsAttempted) {
      loadPayments();
    }
  }

  function handleDonationClick(donation: DonationRequest) {
    if (donation.uuid) {
      push(`/my-donation/${donation.uuid}`);
    }
  }

  function handleDonationsPageChange(event: CustomEvent<{ page: number }>) {
    donationsCurrentPage = event.detail.page;
    loadDonationRequests(true); // Force reload on page change
  }

  function handlePaymentsPageChange(event: CustomEvent<{ page: number }>) {
    paymentsCurrentPage = event.detail.page;
    loadPayments(true); // Force reload on page change
  }

  function formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    return EmployeeDonationService.formatDate(dateString);
  }

  function getStatusClass(status: string): string {
    const statusMap: Record<string, string> = {
      'PAID': 'status-approved',
      'APPROVED': 'status-approved',
      'COMPLETED': 'status-approved',
      'VERIFIED': 'status-approved',
      'REJECTED': 'status-rejected',
      'PENDING': 'status-pending',
    };
    return statusMap[status] || 'status-default';
  }

  const donationsColumns = [
    { key: 'created_at', label: 'Date', render: (value: string) => formatDate(value) },
    { key: 'employer', label: 'Employer', render: (value: any, row: DonationRequest) => row.employer?.name || 'N/A' },
    { key: 'leave_hours', label: 'Hours', render: (value: number) => `${value} hrs` },
    { key: 'leave_value', label: 'Value', render: (value: number) => `$${value?.toFixed(2) || '0.00'}` },
    {
      key: 'donation_request_status',
      label: 'Status',
      align: 'center',
      render: (value: any, row: DonationRequest) => {
        const status = row.donation_request_status?.name || 'N/A';
        const statusClass = getStatusClass(status);
        return `<span class="status-badge ${statusClass}">${status}</span>`;
      }
    }
  ];

  const paymentsColumns = [
    { key: 'created_at', label: 'Date', render: (value: string) => formatDate(value) },
    { key: 'employer', label: 'Employer', render: (value: any, row: DonationRequest) => row.employer?.name || 'N/A' },
    { key: 'leave_hours', label: 'Hours', render: (value: number) => `${value} hrs` },
    { key: 'leave_value', label: 'Value', render: (value: number) => `$${value?.toFixed(2) || '0.00'}` },
    {
      key: 'donation_request_status',
      label: 'Status',
      align: 'center',
      render: (value: any, row: DonationRequest) => {
        const status = row.donation_request_status?.name || 'N/A';
        const statusClass = getStatusClass(status);
        return `<span class="status-badge ${statusClass}">${status}</span>`;
      }
    }
  ];

  // Load charity when charityId changes
  $: {
    if (charityId && charityId !== currentCharityId) {
      // Reset state when charityId changes
      error = null;
      charity = null;
      donationRequests = [];
      payments = [];
      donationsCurrentPage = 1;
      paymentsCurrentPage = 1;
      donationsAttempted = false; // Reset attempt flags
      paymentsAttempted = false;
      loadCharityDetails(charityId);
    } else if (!charityId && !loading && !charity) {
      // Only set error if we've actually tried to load (i.e., not initial mount)
      if (currentCharityId !== '') {
        error = 'No charity ID provided';
      }
    }
  }

  // Load donation requests when tab is active and charity is loaded
  // Only load once per charityId change (donationsAttempted flag prevents retries)
  $: if (charity && activeTab === 'donations' && donationRequests.length === 0 && !donationsLoading && !donationsAttempted) {
    loadDonationRequests();
  }

  // Load payments when tab is active and charity is loaded
  // Only load once per charityId change (paymentsAttempted flag prevents retries)
  $: if (charity && activeTab === 'payments' && payments.length === 0 && !paymentsLoading && !paymentsAttempted) {
    loadPayments();
  }

  // Also try to load on mount as fallback
  onMount(() => {
    if (charityId && !charity && !loading) {
      loadCharityDetails(charityId);
    }
  });

  function handleDonateClick() {
    showDonationModal = true;
  }

  function handleModalClose() {
    showDonationModal = false;
  }

  function handleDonationComplete() {
    showDonationModal = false;
    // Reload donation requests after successful donation
    if (charityId) {
      loadDonationRequests(true);
    }
  }
</script>

<div class="charity-detail-page">
  <BackButton text="Back" onClick={() => window.history.back()} />
  
  {#if loading}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading charity details...</p>
    </div>
  {:else if error}
    <div class="error-container">
      <p class="error-message">{error}</p>
      <Button variant="primary" on:click={() => loadCharityDetails(charityId)}>Try Again</Button>
    </div>
  {:else if charity}
    <div class="detail-container">
      <!-- Charity Information Section -->
      <div class="info-section">
        <div class="section-header">
          <h2>Charity Information</h2>
          {#if charity.verified}
            <span class="verified-badge">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              Verified
            </span>
          {/if}
        </div>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Charity Name</span>
            <span class="value-large">{charity.name || 'N/A'}</span>
          </div>
          {#if charity.abn || charity.charity_government_id}
            <div class="info-item">
              <span class="info-label">ABN</span>
              <span>{charity.abn || charity.charity_government_id}</span>
            </div>
          {/if}
          {#if charity.category?.name}
            <div class="info-item">
              <span class="info-label">Category</span>
              <span>{charity.category.name}</span>
            </div>
          {/if}
          {#if charity.contact_email}
            <div class="info-item">
              <span class="info-label">Contact Email</span>
              <span>
                <a href="mailto:{charity.contact_email}" class="email-link">
                  {charity.contact_email}
                </a>
              </span>
            </div>
          {/if}
          {#if charity.contact_phone}
            <div class="info-item">
              <span class="info-label">Contact Phone</span>
              <span>
                <a href="tel:{charity.contact_phone}" class="phone-link">
                  {charity.contact_phone}
                </a>
              </span>
            </div>
          {/if}
          {#if charity.head_office_location}
            <div class="info-item">
              <span class="info-label">Head Office Location</span>
              <span>{charity.head_office_location}</span>
            </div>
          {/if}
          {#if charity.website}
            <div class="info-item">
              <span class="info-label">Website</span>
              <span>
                <a href={charity.website} target="_blank" rel="noopener noreferrer" class="website-link">
                  {charity.website}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                    <polyline points="15 3 21 3 21 9"></polyline>
                    <line x1="10" y1="14" x2="21" y2="3"></line>
                  </svg>
                </a>
              </span>
            </div>
          {/if}
          {#if charity.description}
            <div class="info-item full-width">
              <span class="info-label">Description</span>
              <span>{charity.description}</span>
            </div>
          {/if}
        </div>
      </div>

      <!-- Donate Button -->
      <div class="donate-section">
        <Button 
          variant="primary" 
          on:click={handleDonateClick}
          class="donate-button"
        >
          Donate to This Charity
        </Button>
      </div>

      <!-- Donation Requests Section -->
      <div class="section-header">
        <h2>Donation Requests</h2>
      </div>

      <!-- Donation Requests Content -->
      <div class="tab-content">
        {#if donationsLoading}
            <div class="loading-container">
              <div class="loading-spinner"></div>
              <p>Loading donation requests...</p>
            </div>
          {:else if donationsError}
            <div class="error-container">
              <p class="error-message">{donationsError}</p>
              <Button variant="primary" on:click={() => loadDonationRequests(true)}>Try Again</Button>
            </div>
          {:else if donationRequests.length === 0}
            <div class="empty-state">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="empty-icon">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
              </svg>
              <h3>No Donation Requests Found</h3>
              <p>You haven't made any donation requests to this charity yet.</p>
            </div>
          {:else}
            <div class="table-section">
              <Table 
                columns={donationsColumns}
                data={donationRequests}
                onRowClick={handleDonationClick}
              />
            </div>
            {#if donationsTotalPages > 1}
              <div class="pagination-container">
                <Pagination 
                  currentPage={donationsCurrentPage} 
                  totalPages={donationsTotalPages} 
                  on:pageChange={handleDonationsPageChange} 
                />
              </div>
            {/if}
          {/if}
      </div>
    </div>
  {/if}

  <!-- Donation Request Modal -->
  <DonationRequestModal 
    isOpen={showDonationModal}
    preSelectedCharityUuid={charityId}
    on:close={handleModalClose}
    on:complete={handleDonationComplete}
  />
</div>

<style>
  .charity-detail-page {
    padding: 2rem;
    max-width: 1000px;
    margin: 0 auto;
    background: #f7fafc;
    min-height: 100vh;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .loading-spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #f3f4f6;
    border-top: 3px solid var(--color-primary, #f0b429);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .error-message {
    color: var(--color-negative, #dc2626);
    font-size: 1.1rem;
    margin-bottom: 1rem;
  }

  .detail-container {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .info-section {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
  }

  .section-header h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  .info-section h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  .verified-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.75rem;
    background: #d1fae5;
    color: #065f46;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
  }

  .verified-badge svg {
    width: 12px;
    height: 12px;
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
  }

  .info-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .info-item.full-width {
    grid-column: 1 / -1;
  }

  .info-item .info-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
  }

  .info-item span {
    font-size: 1rem;
    color: #111827;
    font-weight: 400;
  }

  .value-large {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
  }

  .email-link,
  .phone-link,
  .website-link {
    color: var(--color-primary, #3b82f6);
    text-decoration: none;
    transition: color 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
  }

  .email-link:hover,
  .phone-link:hover,
  .website-link:hover {
    color: var(--color-primary-hover, #2563eb);
    text-decoration: underline;
  }

  .website-link svg {
    flex-shrink: 0;
  }

  .donate-section {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid #e5e7eb;
  }

  .tab-content {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .table-section {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .empty-icon {
    color: #d1d5db;
    margin-bottom: 1.5rem;
  }

  .empty-state h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 0.5rem;
  }

  .empty-state p {
    color: #6b7280;
    margin-bottom: 2rem;
    max-width: 400px;
    font-size: 0.875rem;
  }

  .pagination-container {
    display: flex;
    justify-content: center;
    margin-top: 1.5rem;
  }

  /* Status Badges */
  :global(.status-badge) {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
  }

  :global(.status-approved) {
    background: #d1fae5;
    color: #065f46;
  }

  :global(.status-rejected) {
    background: #fee2e2;
    color: #991b1b;
  }

  :global(.status-pending) {
    background: #fef3c7;
    color: #92400e;
  }

  :global(.status-default) {
    background: #f3f4f6;
    color: #4b5563;
  }

  @media (max-width: 768px) {
    .charity-detail-page {
      padding: 1rem;
    }

    .info-grid {
      grid-template-columns: 1fr;
    }

  }
</style>

